import { IconAccount, IconBook, IconBriefcase, IconDatasets } from "dash-styled"
import React from "react"
import { useBoolean } from "../../../../../Hooks/useBoolean"
import { abstractRoutePaths, routePaths } from "../../../../../Routes"
import { CompanyLogo } from "../../../../Common/CompanyLogo/CompanyLogo"
import { AccountMenu } from "./AccountMenu"
import { MenuItem } from "./MenuItem"
import { matchPath } from "react-router"
import { useAppState } from "../../../../../Hooks/useReduxState"
import { ReactComponent as IconPeople } from "icons/lib/people.svg"
import { ReactComponent as IconBackoffice } from "icons/lib/backoffice.svg"
import { hasValue } from "../../../../../Models/Client/FetchState"
import { useMyOrgRole } from "../../../../../Hooks/useMyOrgRole"
import { ReactComponent as IconGear } from "icons/lib/gear.svg"
import { selectUserName } from "../../../../../Helpers/Functions/Selectors/auth"
import { hasOrgPermission } from "../../../../../Models/Client/OrgPermissionScope"
import { useBackofficeAdmin } from "../../../../../Hooks/useBackofficeAdmin"

interface MenuProps {
    orgUsername: string
}

export function Menu({ orgUsername }: MenuProps) {
    const [accountMenuShown, showAccountMenu, hideAccountMenu] =
        useBoolean(false)
    const orgRoleFetch = useMyOrgRole(orgUsername)
    const currentPath = useAppState(state => state.router.location.pathname)
    const isPersonalAccount = useAppState(selectUserName) === orgUsername
    const backofficeAdminFetch = useBackofficeAdmin()

    if (!hasValue(orgRoleFetch) || !hasValue(backofficeAdminFetch)) {
        return null
    }

    const isBackofficeAdmin = backofficeAdminFetch.value && isPersonalAccount

    return (
        <ul
            className="h-full list-none flex flex-col flex-grow m-0 px-0 text-xs items-center justify-stretch py-4"
            data-testid="projects-menu"
        >
            <li className="w-full">
                <CompanyLogo
                    variant="symbol"
                    size="sm"
                    className="my-8 px-5"
                    glow="dark"
                />
            </li>
            <li className="w-full">
                <MenuItem
                    to={routePaths.appList(orgUsername)}
                    type="spa"
                    isActive={
                        matchPath(
                            currentPath,
                            routePaths.appList(orgUsername)
                        ) !== null
                    }
                >
                    <IconBriefcase size="xxl" />
                    <span className="py-1">Projects</span>
                </MenuItem>
            </li>
            {!isPersonalAccount &&
                hasOrgPermission(orgRoleFetch.value, "member:view") && (
                    <li className="w-full">
                        <MenuItem
                            to={routePaths.orgMemberList(orgUsername)}
                            type="spa"
                            isActive={
                                matchPath(
                                    currentPath,
                                    abstractRoutePaths.orgMemberList
                                ) !== null
                            }
                        >
                            <IconPeople className="w-7 h-7" />
                            <span className="py-1">Members</span>
                        </MenuItem>
                    </li>
                )}
            {hasOrgPermission(orgRoleFetch.value, "worker:view") && (
                <li className="w-full">
                    <MenuItem
                        to={routePaths.orgWorkerList(orgUsername)}
                        type="spa"
                        isActive={
                            matchPath(
                                currentPath,
                                abstractRoutePaths.orgWorkerList
                            ) !== null ||
                            matchPath(
                                currentPath,
                                abstractRoutePaths.orgWorkerGroupList
                            ) !== null
                        }
                    >
                        <IconGear className="w-7 h-7" />
                        <span className="py-1">Workers</span>
                    </MenuItem>
                </li>
            )}
            {isPersonalAccount && (
                <li className="w-full">
                    <MenuItem
                        to={routePaths.datasets(orgUsername)}
                        type="spa"
                        isActive={
                            matchPath(
                                currentPath,
                                abstractRoutePaths.datasets
                            ) !== null
                        }
                    >
                        <IconDatasets size="xxl" />
                        <span className="py-1">Storage</span>
                    </MenuItem>
                </li>
            )}
            <li className="flex-grow w-full">
                <MenuItem
                    to={routePaths.docs()}
                    type="samedomain"
                    isActive={false}
                >
                    <IconBook size="xxl" />
                    <span className="py-1">Docs</span>
                </MenuItem>
            </li>

            {isBackofficeAdmin && (
                <li
                    className="flex-grow w-full"
                    data-testid="backoffice-menu-item"
                >
                    <MenuItem
                        to={routePaths.customers()}
                        type="spa"
                        isActive={
                            matchPath(
                                currentPath,
                                abstractRoutePaths.customers
                            ) !== null ||
                            matchPath(
                                currentPath,
                                abstractRoutePaths.customerDetails
                            ) !== null
                        }
                    >
                        <IconBackoffice className="w-7 h-7" />
                        <span className="py-1">Backoffice</span>
                    </MenuItem>
                </li>
            )}
            <li className="w-full relative">
                <div
                    className="text-white w-full p-4 flex flex-col items-center cursor-pointer"
                    onMouseEnter={showAccountMenu}
                    onMouseLeave={hideAccountMenu}
                    onClick={showAccountMenu}
                    data-testid="user-info-item"
                >
                    <IconAccount size="xxl" />
                    {accountMenuShown ? (
                        <AccountMenu
                            orgUsername={orgUsername}
                            className="ml-16 absolute left-0 bottom-0 text-black"
                        />
                    ) : null}
                </div>
            </li>
        </ul>
    )
}
